import React, { useEffect, useRef } from "react";
import { Spinner } from "@material-tailwind/react";
import { PrimaryButton } from "../../../../components/Buttons";
import { FormModal } from "../../../../components/form";

/**
 * Consolidated payment-receipt preview modal. Replaces the five
 * near-identical TokenPayment / DownPayment / FullPayment / ScheduledPayment
 * / SlabWisePayment FormModal files, which differed only by the title
 * string. The body is an A4-sized iframe rendering the HTML receipt at
 * 0.5× scale; clicking Send fires `onSendDocument`.
 *
 * The mapping below is the BE's CustomerSalesPaymentType enum (see
 * constants/enums.js → CUSTOMER_SALES_PAYMENT_TYPE). Pass `paymentType` as
 * either the numeric id (1..5) or the string code ("TOKEN_PAYMENT", etc.);
 * an explicit `title` prop overrides both.
 */
const TITLES = {
    TOKEN_PAYMENT: "Token Payment Receipt Preview",
    DOWN_PAYMENT: "Down Payment Receipt Preview",
    SCHEDULED_PAYMENT: "Scheduled Payment Receipt Preview",
    FULL_PAYMENT: "Full Payment Receipt Preview",
    SLAB_WISE_PAYMENT: "Slab Wise Payment Receipt Preview",
};
const NUMERIC_TO_CODE = {
    1: "TOKEN_PAYMENT",
    2: "SCHEDULED_PAYMENT",
    3: "FULL_PAYMENT",
    4: "DOWN_PAYMENT",
    5: "SLAB_WISE_PAYMENT",
};

const PaymentReceiptPreviewModal = ({
    open,
    onClose,
    paymentType,
    title,
    data,
    onSendDocument,
    sendLoading,
}) => {
    const iframeRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        if (iframeRef.current && data) {
            const doc = iframeRef.current.contentWindow.document;
            doc.open();
            doc.write(data); // Full HTML doc (with <html>/<head>/<body>)
            doc.close();
        }
    }, [data, open]);

    const code = typeof paymentType === "number" ? NUMERIC_TO_CODE[paymentType] : paymentType;
    const resolvedTitle = title || TITLES[code] || "Payment Receipt Preview";

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={resolvedTitle}
            size="2xl"
            footer={
                <div className="flex justify-end">
                    <PrimaryButton className="w-32" onClick={onSendDocument} disabled={sendLoading}>
                        {sendLoading ? <Spinner color="gray" /> : "Send"}
                    </PrimaryButton>
                </div>
            }
        >
            <div className="border rounded-lg bg-white flex flex-col items-center w-full overflow-hidden">
                <iframe
                    ref={iframeRef}
                    title={resolvedTitle}
                    style={{
                        width: "794px",        // A4 width in px @ 96dpi
                        minHeight: "1123px",   // A4 height
                        border: "none",
                        background: "white",
                        margin: "20px 0",
                        transform: "scale(0.5)",
                        transformOrigin: "top center",
                    }}
                />
            </div>
        </FormModal>
    );
};

export default PaymentReceiptPreviewModal;
